import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  CommandInput,
  CommandList,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  Button,
  toast,
} from "@exiftrator/shared/components/ui";
import { MapPin, Loader, Save } from "lucide-react";
import { MapContainer, TileLayer, CircleMarker, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useLocations } from "../../contexts/LocationsContext";
import { searchLocationsNormalized } from "../../services/nominatimService";
import useFocusRestore from "../../hooks/useFocusRestore";
import SplitCommandModalShell from "./SplitCommandModalShell";
import {
  formatLocationCoords,
  getApplyActionLabel,
  getSelectedTargetCount,
} from "./modalUtils";

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [delay, value]);

  return debouncedValue;
}

function LocationPickerModal({
  open,
  onOpenChange,
  selectedIndices,
  selectedCellRange,
  onApplyLocation,
  onSaveLocation,
}) {
  const { getAllLocations, locationNameExists } = useLocations();

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [lastCompletedQuery, setLastCompletedQuery] = useState("");
  const [selectedOptionKey, setSelectedOptionKey] = useState(null);
  const [isNamingSearchResult, setIsNamingSearchResult] = useState(false);
  const [customName, setCustomName] = useState("");

  const debouncedQuery = useDebounce(searchQuery, 350);
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const normalizedDebouncedQuery = debouncedQuery.trim().toLowerCase();
  const hasCompletedCurrentSearch =
    normalizedSearchQuery.length >= 3 &&
    lastCompletedQuery === normalizedSearchQuery;
  const showLoadingState =
    normalizedSearchQuery.length > 0 &&
    (normalizedSearchQuery !== normalizedDebouncedQuery ||
      isSearching ||
      (normalizedSearchQuery.length >= 3 && !hasCompletedCurrentSearch));

  useFocusRestore(open);

  const selectedCount = useMemo(
    () => getSelectedTargetCount(selectedIndices, selectedCellRange),
    [selectedCellRange, selectedIndices],
  );

  const savedLocations = useMemo(() => getAllLocations(), [getAllLocations]);

  const filteredSavedLocations = useMemo(() => {
    if (!normalizedSearchQuery) {
      return savedLocations;
    }

    return savedLocations.filter(
      (location) =>
        location.name.toLowerCase().includes(normalizedSearchQuery) ||
        location.address?.toLowerCase().includes(normalizedSearchQuery) ||
        `${location.latitude}, ${location.longitude}`.includes(
          normalizedSearchQuery,
        ),
    );
  }, [normalizedSearchQuery, savedLocations]);

  const savedOptions = useMemo(
    () =>
      filteredSavedLocations.map((location) => ({
        key: `saved:${location.id}`,
        source: "saved",
        ...location,
      })),
    [filteredSavedLocations],
  );

  const visibleSearchResults = hasCompletedCurrentSearch ? searchResults : [];

  const searchOptions = useMemo(
    () =>
      visibleSearchResults.map((location) => ({
        key: `search:${location.id}`,
        source: "search",
        ...location,
      })),
    [visibleSearchResults],
  );

  const allOptions = useMemo(
    () => [...savedOptions, ...searchOptions],
    [savedOptions, searchOptions],
  );

  const selectedOption = useMemo(
    () => allOptions.find((option) => option.key === selectedOptionKey) || null,
    [allOptions, selectedOptionKey],
  );
  const selectedAddress =
    selectedOption?.address || selectedOption?.name || null;

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSearchResults([]);
      setLastCompletedQuery("");
      setSelectedOptionKey(null);
      setIsNamingSearchResult(false);
      setCustomName("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (allOptions.length === 0) {
      setSelectedOptionKey(null);
      return;
    }

    const hasSelection = allOptions.some(
      (option) => option.key === selectedOptionKey,
    );
    if (!hasSelection) {
      setSelectedOptionKey(allOptions[0].key);
    }
  }, [allOptions, open, selectedOptionKey]);

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      setLastCompletedQuery("");
      return;
    }

    let cancelled = false;
    const normalizedDebouncedQuery = debouncedQuery.trim().toLowerCase();

    const loadSearchResults = async () => {
      setIsSearching(true);
      try {
        const results = await searchLocationsNormalized(debouncedQuery, {
          limit: 5,
        });
        if (!cancelled) {
          setSearchResults(results);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Location search error:", error);
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
          setLastCompletedQuery(normalizedDebouncedQuery);
        }
      }
    };

    loadSearchResults();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const handleSelectOption = useCallback((option) => {
    setSelectedOptionKey(option.key);
    setIsNamingSearchResult(false);
    setCustomName("");
  }, []);
  const handleCommandValueChange = useCallback(
    (value) => {
      if (!value || value === selectedOptionKey) {
        return;
      }

      const option = allOptions.find((item) => item.key === value);
      if (option) {
        handleSelectOption(option);
      }
    },
    [allOptions, handleSelectOption, selectedOptionKey],
  );

  const handleApplySelected = useCallback(() => {
    if (!selectedOption) return;

    if (selectedCount === 0) {
      toast.info("Select files to apply location");
      return;
    }

    onApplyLocation?.(selectedOption);
    onOpenChange(false);
  }, [onApplyLocation, onOpenChange, selectedCount, selectedOption]);

  const startSaveFlow = useCallback(() => {
    if (!selectedOption || selectedOption.source !== "search") {
      return;
    }

    const suggestedName =
      selectedOption.primaryName || selectedOption.name.split(",")[0].trim();
    setIsNamingSearchResult(true);
    setCustomName(suggestedName);
  }, [selectedOption]);

  const handleSaveSelected = useCallback(
    (applyAfterSave = false) => {
      if (!selectedOption || selectedOption.source !== "search") {
        return;
      }

      const trimmedName = customName.trim();
      if (!trimmedName) {
        return;
      }

      if (locationNameExists(trimmedName)) {
        return;
      }

      const savePayload = {
        ...selectedOption,
        customName: trimmedName,
        address: selectedOption.address || selectedOption.name,
      };

      const saveResult = onSaveLocation?.(savePayload);
      if (saveResult === false) {
        return;
      }

      if (applyAfterSave && selectedCount > 0) {
        onApplyLocation?.(selectedOption);
        toast.success(`Saved and applied "${trimmedName}"`);
      } else {
        toast.success(`Saved "${trimmedName}"`);
      }

      onOpenChange(false);
    },
    [
      customName,
      locationNameExists,
      onApplyLocation,
      onOpenChange,
      onSaveLocation,
      selectedCount,
      selectedOption,
    ],
  );

  const normalizedSelectedAddress =
    typeof selectedAddress === "string"
      ? selectedAddress.trim().toLowerCase()
      : "";
  const normalizedSelectedName =
    typeof selectedOption?.name === "string"
      ? selectedOption.name.trim().toLowerCase()
      : "";
  const isDuplicateName =
    Boolean(customName.trim()) && locationNameExists(customName.trim());
  const isSaveMode =
    Boolean(selectedOption) &&
    selectedOption.source === "search" &&
    isNamingSearchResult;
  const canSaveLocation = Boolean(customName.trim()) && !isDuplicateName;
  const canApplySelection = selectedCount > 0 && Boolean(selectedOption);
  const shouldShowAddress =
    Boolean(normalizedSelectedAddress) &&
    normalizedSelectedAddress !== normalizedSelectedName;
  const applyLabel = getApplyActionLabel(selectedCount, "Apply location");

  const handlePrimaryAction = useCallback(() => {
    if (isSaveMode) {
      handleSaveSelected(selectedCount > 0);
      return;
    }

    handleApplySelected();
  }, [handleApplySelected, handleSaveSelected, isSaveMode, selectedCount]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      const activeElement = event.target;
      const isNameInput =
        activeElement?.dataset?.role === "location-name-input";

      if (event.key === "Enter" && !isNameInput) {
        event.preventDefault();
        handlePrimaryAction();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handlePrimaryAction, open]);

  const results = (
    <CommandList className="min-h-0 max-h-none overflow-y-auto">
      {savedOptions.length > 0 && (
        <CommandGroup heading="Saved Locations">
          {savedOptions.map((option) => (
            <CommandItem
              key={option.key}
              value={option.key}
              onSelect={() => handleSelectOption(option)}
              className="cursor-pointer transition-colors hover:bg-accent/60 hover:text-accent-foreground"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">
                  {option.name}
                </span>
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {savedOptions.length > 0 && searchOptions.length > 0 && (
        <CommandSeparator />
      )}

      {searchOptions.length > 0 && (
        <CommandGroup heading="Search Results">
          {searchOptions.map((option) => (
            <CommandItem
              key={option.key}
              value={option.key}
              onSelect={() => handleSelectOption(option)}
              className="cursor-pointer transition-colors hover:bg-accent/60 hover:text-accent-foreground"
            >
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-sm font-medium">
                  {option.primaryName || option.name}
                </span>
                {option.secondaryText ? (
                  <span className="truncate text-xs text-muted-foreground">
                    {option.secondaryText}
                  </span>
                ) : null}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {savedOptions.length === 0 &&
        searchOptions.length === 0 &&
        !showLoadingState && (
          <div className="py-10 text-center">
            <MapPin className="mx-auto mb-2 size-8 text-muted-foreground" />
            {normalizedSearchQuery.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Search for an address or place name.
              </p>
            ) : normalizedSearchQuery.length < 3 ? (
              <p className="text-sm text-muted-foreground">
                Type at least 3 characters.
              </p>
            ) : hasCompletedCurrentSearch ? (
              <p className="text-sm text-muted-foreground">
                No locations found for this search.
              </p>
            ) : null}
          </div>
        )}

      {showLoadingState && (
        <div className="py-10 text-center">
          <Loader className="mx-auto mb-2 size-6 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Searching...</p>
        </div>
      )}
    </CommandList>
  );

  const preview = selectedOption ? (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
        <div className="h-[300px] overflow-hidden rounded-md border border-border-subtle bg-muted/30">
          <StaticLocationMapPreview
            latitude={selectedOption.latitude}
            longitude={selectedOption.longitude}
          />
        </div>

        {shouldShowAddress && (
          <p className="text-xs text-muted-foreground">{selectedAddress}</p>
        )}

        <p className="text-xs font-mono text-muted-foreground">
          {formatLocationCoords(
            selectedOption.latitude,
            selectedOption.longitude,
          )}
        </p>

        {selectedOption.source === "search" &&
          (!isNamingSearchResult ? (
            <Button
              variant="outline"
              className="w-full"
              onClick={startSaveFlow}
            >
              <Save data-icon="inline-start" className="size-4" />
              Save location
            </Button>
          ) : (
            <div className="space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">
                Location name
              </label>
              <input
                data-role="location-name-input"
                type="text"
                value={customName}
                onChange={(event) => setCustomName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (canSaveLocation) {
                      handleSaveSelected(selectedCount > 0);
                    }
                  } else if (event.key === "Escape") {
                    event.preventDefault();
                    setIsNamingSearchResult(false);
                  }
                }}
                className={`h-9 w-full rounded-md border bg-background px-3 text-sm text-foreground outline-none transition-colors ${isDuplicateName ? "border-destructive" : "border-input focus:border-ring"}`}
                placeholder="Location name"
              />
              {isDuplicateName && (
                <p className="text-xs text-destructive">
                  A location with this name already exists.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {selectedCount > 0
                  ? "Use Save and Apply in the footer to commit this location."
                  : "Use Save Location in the footer to add this location."}
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => setIsNamingSearchResult(false)}
              >
                Cancel saving
              </Button>
            </div>
          ))}
      </div>
    </div>
  ) : (
    <div className="flex h-full min-h-0 flex-col p-4">
      <div className="relative h-[300px] overflow-hidden rounded-md border border-border-subtle bg-muted/40">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,hsl(var(--muted))_0%,transparent_40%),radial-gradient(circle_at_80%_30%,hsl(var(--muted))_0%,transparent_38%),linear-gradient(135deg,hsl(var(--muted))_0%,hsl(var(--background))_100%)]" />
        <div className="absolute inset-0 backdrop-blur-[2px]" />
      </div>
    </div>
  );

  const footer = (
    <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
      <span className="text-xs text-muted-foreground">Esc to close</span>
      <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center md:justify-end">
        <Button
          variant="outline"
          size="sm"
          className="hidden md:inline-flex"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="w-full md:w-auto"
          onClick={handlePrimaryAction}
          disabled={isSaveMode ? !canSaveLocation : !canApplySelection}
        >
          {isSaveMode
            ? selectedCount > 0
              ? "Save and Apply"
              : "Save Location"
            : applyLabel}
        </Button>
      </div>
    </div>
  );

  return (
    <SplitCommandModalShell
      open={open}
      onOpenChange={onOpenChange}
      title="Location picker"
      description="Search and apply a location"
      search={
        <CommandInput
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Search for a place or address..."
        />
      }
      results={results}
      preview={preview}
      footer={footer}
      mobileResultsLabel="Results"
      mobilePreviewLabel="Map"
      commandProps={{
        shouldFilter: false,
        className: "bg-transparent",
        value: selectedOptionKey || "",
        onValueChange: handleCommandValueChange,
      }}
    />
  );
}

function StaticLocationMapPreview({ latitude, longitude }) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return (
      <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
        Invalid coordinates
      </div>
    );
  }

  return (
    <MapContainer
      center={[latitude, longitude]}
      zoom={13}
      style={{ height: "100%", width: "100%" }}
      zoomControl={false}
      scrollWheelZoom={false}
      dragging={false}
      touchZoom={false}
      doubleClickZoom={false}
      boxZoom={false}
      keyboard={false}
      attributionControl={false}
      className="cursor-default"
    >
      <MapViewportSync latitude={latitude} longitude={longitude} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <CircleMarker
        center={[latitude, longitude]}
        radius={8}
        pathOptions={{ color: "#3b82f6", fillOpacity: 0.85 }}
      />
    </MapContainer>
  );
}

function MapViewportSync({ latitude, longitude }) {
  const map = useMap();

  useEffect(() => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    map.setView([latitude, longitude], map.getZoom(), {
      animate: false,
    });
  }, [latitude, longitude, map]);

  return null;
}

export default LocationPickerModal;
